import type {
	MenuItemLinks,
	NavBarItem,
	NavBarLinkItem,
	NavBarMenuItem,
	NavCta,
} from '@ds-mgnl/marketing';
import { MdLock } from '@dsx/react';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

export const topNavData: NavBarItem[] = [
	{
		type: 'submenu',
		label: 'Solutions',
		items: [
			{
				type: 'links',
				linkGroups: [
					{
						links: [
							{
								label: 'Labor Efficiency',
								path: '/solutions/labor-efficiency',
								value: '/solutions/labor-efficiency',
								openInNewTab: false,
							},
							{
								label: 'Compliance',
								path: '/solutions/compliance',
								value: '/solutions/compliance',
								openInNewTab: false,
							},
							{
								label: 'Capital Management',
								path: '/solutions/capital-management',
								value: '/solutions/capital-management',
								openInNewTab: false,
							},
							{
								label: 'Routine & Emergency Repairs',
								path: '/services/routine-and-emergency-repairs',
								value: '/services/routine-and-emergency-repairs',
								openInNewTab: false,
							},
							{
								label: 'Recurring Maintenance Programs',
								path: '/services/recurring-maintenance-programs',
								value: '/services/recurring-maintenance-programs',
								openInNewTab: false,
							},
							{
								label: 'Planned Projects',
								path: '/services/planned-projects',
								value: '/services/planned-projects',
								openInNewTab: false,
							},
						],
					},
				],
			} as MenuItemLinks,
		],
	} as NavBarMenuItem,
	{
		type: 'submenu',
		label: 'Products',
		items: [
			{
				type: 'links',
				linkGroups: [
					{
						links: [
							{
								label: 'TELS Platform',
								path: '/products/tels-platform',
								value: '/products/tels-platform',
								openInNewTab: false,
							},
							{
								label: 'Maintenance',
								path: '/products/maintenance',
								value: '/products/maintenance',
								openInNewTab: false,
							},
							{
								label: 'Housekeeping',
								path: '/products/housekeeping',
								value: '/products/housekeeping',
								openInNewTab: false,
							},
							{
								label: 'TELS Building Services',
								path: '/services',
								value: '/services',
								openInNewTab: false,
							},
							{
								label: 'Third Party Integrations',
								path: '/products/third-party-integrations',
								value: '/products/third-party-integrations',
								openInNewTab: false,
							},
						],
					},
				],
			} as MenuItemLinks,
		],
	} as NavBarMenuItem,
	{
		type: 'submenu',
		label: 'Resources',
		items: [
			{
				type: 'links',
				linkGroups: [
					{
						links: [
							{
								label: 'Expertise',
								path: '/resources/blogs',
								value: '/resources/blogs',
								openInNewTab: false,
							},
							{
								label: 'Case Studies',
								path: '/resources/case-studies',
								value: '/resources/case-studies',
								openInNewTab: false,
							},
							{
								label: 'Regulatory Guides',
								path: '/resources/regulatory-guides',
								value: '/resources/regulatory-guides',
								openInNewTab: false,
							},
							{
								label: 'All Resources',
								path: '/resources',
								value: '/resources',
								openInNewTab: false,
							},
						],
					},
				],
			} as MenuItemLinks,
		],
	} as NavBarMenuItem,
];

export const topNavServiceProviderData: NavBarItem[] = [
	{
		type: 'link',
		link: {
			label: 'Service providers',
			path: '/service-provider/',
			value: '/service-provider/',
			openInNewTab: false,
		},
	} as NavBarLinkItem,
	{
		type: 'link',
		link: {
			label: 'Training',
			path: '/service-provider/training/',
			value: '/service-provider/training/',
			openInNewTab: false,
		},
	} as NavBarLinkItem,
	{
		type: 'submenu',
		label: 'Resources ',
		items: [
			{
				type: 'links',
				linkGroups: [
					{
						links: [
							{
								label: 'TELS Connect Walkthrough',
								path: '/collateral-library/tels-connect-tech-overview-17877',
								value: '/collateral-library/tels-connect-tech-overview-17877',
								openInNewTab: false,
							},
							{
								label: 'Expectations',
								path: '/collateral-library/tels-building-services-interior-service-provider-expectations',
								value:
									'/collateral-library/tels-building-services-interior-service-provider-expectations',
								openInNewTab: false,
							},
						],
					},
				],
			} as MenuItemLinks,
		],
	} as NavBarMenuItem,
	{
		type: 'link',
		link: {
			label: '888-225-7729',
			path: 'tel:888-225-7729',
			value: 'tel:888-225-7729',
			openInNewTab: false,
		},
	} as NavBarLinkItem,
];

const MenuVariantPaths = {
	CONNECT: '/connect',
	SERVICE_PROVIDER: '/service-provider',
};

const registerLink =
	'https://smart.tels.net/SMART/ServiceProviderPortal/Registration?utm_source=sign-up-top&utm_medium=landing-page&utm_campaign=rsa';

const isPathMatch = (pathName: string, value: string) => {
	return pathName.startsWith(value);
};

const getCtaLinks = (pathname: string): NavCta[] => {
	if (isPathMatch(pathname, MenuVariantPaths.CONNECT)) {
		return [
			{
				path: 'register',
				label: 'Register',
				value: registerLink,
				openInNewTab: true,
			},
			{
				path: 'sign-in',
				label: 'Sign in',
				value: 'https://smart.tels.net/SMART/ServiceProviderPortal',
				openInNewTab: true,
				icon: MdLock,
			},
		];
	}

	return [
		{
			path: 'contact/',
			label: 'Contact us',
			value: 'contact/',
			openInNewTab: false,
		},
		{
			path: 'sign-in',
			label: 'Sign in',
			value: isPathMatch(pathname, MenuVariantPaths.SERVICE_PROVIDER)
				? 'https://smart.tels.net/SMART/ServiceProviderPortal'
				: 'https://www.tels.net/TELSLogin/signin.aspx',
			openInNewTab: true,
			icon: MdLock,
		},
	];
};

export function useNavData() {
	const { pathname } = useLocation();
	const ctaLinks = useMemo(() => getCtaLinks(pathname), [pathname]);

	// TODO: allow for trailing slash in path
	const navData = useMemo(() => {
		if (isPathMatch(pathname, MenuVariantPaths.CONNECT)) {
			return [];
		}

		if (isPathMatch(pathname, MenuVariantPaths.SERVICE_PROVIDER)) {
			return topNavServiceProviderData;
		}

		return topNavData;
	}, [pathname]);

	return { navData, ctaLinks };
}
